import { camelCase, isEmpty, snakeCase } from 'lodash'

import { isKubernetes as isTargetTypeKubernetes } from 'components/attacks/create/targets/target-utils'
import {
  isConcurrentNode,
  isContinuousStatusCheckNode,
  isDelayNode,
  isFailureFlagNode,
  isSynchronousStatusCheckNode,
  type PossibleGraphNode
} from 'components/scenarios/utils/graph/nodes/typeguards'
import { walkGraph } from 'components/scenarios/utils/graph/walkers'
import { reformatSteps } from 'components/scenarios/utils/step-actions'
import {
  type FailureFlagNode,
  type GraphNode,
  type Scenario,
  type ScenarioGraph,
  ScenarioGraphNodeType,
  type ScenarioStep
} from 'types/api/scenarios/scenario'
import { isLastIndex } from 'utils/list'
import { cleanObjKeysAndValues, keysToCamelCase } from 'utils/obj'

import type { ScenarioRunResponse } from 'types/api/scenarios/runs/scenario-runs'

export const concurrentGraphTypes = [
  ScenarioGraphNodeType.Concurrent,
  ScenarioGraphNodeType.ContinuousStatusCheck
]
export const synchronousGraphTypes = [
  ScenarioGraphNodeType.InfraAttack,
  ScenarioGraphNodeType.FailureFlag,
  ScenarioGraphNodeType.Delay,
  ScenarioGraphNodeType.SynchronousStatusCheck,
  ScenarioGraphNodeType.FailureFlag
]

export const createDelayNode = (changes = {}) => ({
  type: ScenarioGraphNodeType.Delay,
  delay: 5,
  ...changes
})

export const scenarioHasStatusCheck = (graph) => {
  return reformatSteps(graph).some(
    (node) =>
      isSynchronousStatusCheckNode(node) || isContinuousStatusCheckNode(node)
  )
}

export const hasAttackSteps = (scenario) =>
  getGraphNodesByType(scenario.graph, ScenarioGraphNodeType.InfraAttack)
    .length > 0

export const hasFailureFlagSteps = (scenario: Scenario) =>
  getGraphNodesByType(scenario.graph, ScenarioGraphNodeType.FailureFlag)
    .length > 0

export const getNodeAtIndex = (graph, index) => {
  // This is a simpler but more expensive implementation.
  // If we have have very large scenarios we could just walk the graph
  // rather than converting it to an array.
  return arrayFromGraph(graph)[index]
}

export const getNodeById = (graph, id) => graph.nodes[id]

export const arrayFromGraph = (graph) => {
  const arrayFromWalkGraph = Array.from(walkGraph(graph)).map((node) => {
    if (isConcurrentNode(node)) {
      return {
        ...node,
        branches: node.branches.map(arrayFromGraph)
      }
    }
    return node
  })

  if (isDelayNode(arrayFromWalkGraph[arrayFromWalkGraph.length - 1])) {
    arrayFromWalkGraph.splice(-1, 1)
  }
  return arrayFromWalkGraph
}

export const mapBranchedSteps = (steps, parent) =>
  steps.flatMap((step) => {
    if (step?.branches?.length) {
      return [
        step,
        ...step.branches.flatMap((branch, index) => {
          return mapBranchedSteps(branch, {
            ...step,
            branchId: `${step.id}-${index}` // use the step id as well as the index of the branch
          })
        })
      ]
    }
    if (parent) {
      return {
        ...step,
        branchId: parent.branchId,
        parent
      }
    }

    return step
  })

export const graphFromArray = (nodeArray) => {
  return nodeArray.reduce(
    (list, node, index) => {
      const isLast = isLastIndex(nodeArray, index)

      list.nodes[index] = {
        ...node,
        id: String(index)
      }

      delete list.nodes[index].next

      if (!isLast) {
        list.nodes[index].next = String(index + 1)
      }

      return list
    },
    { nodes: {}, start_id: '0' }
  )
}

export const concurrentGraphFromArray = (nodeArray) => ({
  start_id: 'concurrentNode',
  nodes: {
    concurrentNode: {
      id: 'concurrentNode',
      type: ScenarioGraphNodeType.Concurrent,
      branches: nodeArray
        .filter((node) => !isEmpty(node))
        .map((node) => graphFromArray(node))
    }
  }
})

export const lengthOfScenarioGraph = (graph) => Object.keys(graph.nodes).length

export const getLastScenarioGraphNode = (graph) =>
  Object.values(graph.nodes).find((node) => !node.next)

export function getGraphNodesByType(
  graph: ScenarioGraph,
  ...types: ScenarioGraphNodeType[]
) {
  const typeSet = new Set(types)
  return mapBranchedSteps(arrayFromGraph(graph), undefined).filter((node) =>
    typeSet.has(node.type)
  )
}

export const getGraphObservers = (graph) => {
  return mapBranchedSteps(graph?.observers ?? []).filter(
    (node) => node.type === ScenarioGraphNodeType.ContinuousStatusCheck
  )
}

export const getNextNodeOfType = (graph, start, ...types) => {
  const sequence = walkGraph(graph, start)
  const typeSet = new Set(types)
  let done = false
  while (!done) {
    const current = sequence.next()
    done = current.done
    if (current.value && typeSet.has(current.value.type)) {
      return current.value
    }
  }
}

// This covers the cases for retrieving the infraCommandArgs from the passed in attackConfiguration
// TODO: fix the key casing for the graph nodes
function getInfraCommandArgs(node) {
  if (node.attack_configuration) {
    return (
      node.attack_configuration.command.infraCommandArgs ||
      node.attack_configuration.command.infra_command_args
    )
  }

  if (node.attackConfiguration) {
    return (
      node.attackConfiguration.command.infraCommandArgs ||
      node.attackConfiguration.command.infra_command_args
    )
  }

  return node.impact_definition?.infra_command_args
}

function isServiceAttackNode(node) {
  return Boolean(
    keysToCamelCase(node).attackConfiguration?.targets[0]?.serviceId
  )
}

export function graphNodeToStepAttack(node: PossibleGraphNode) {
  if (
    isFailureFlagNode(node) ||
    isSynchronousStatusCheckNode(node) ||
    isContinuousStatusCheckNode(node) ||
    isConcurrentNode(node) ||
    isDelayNode(node)
  ) {
    return node
  }

  const nodeAttackConfiguration = node?.attack_configuration
  const hasTargetDefinition = Boolean(node?.target_definition)
  const hasImpactDefinition = Boolean(node?.impact_definition)

  const nodeTarget = node?.target_definition ?? {}
  const nodeImpact = node?.impact_definition ?? {}

  // Since Shared and Recommended scenarios have no targets in the attackConfiguration, we still need to default to
  // targetDefinition to extract the targetType
  const targetType =
    nodeAttackConfiguration?.targets[0]?.targetingStrategy?.type ||
    nodeTarget.target_type

  const isKubernetes = isTargetTypeKubernetes(targetType)
  const infraCommandArgs = getInfraCommandArgs(node)

  // Network tags are stored in an object where the key is the tag with an array of values.
  // Casing (camelCase/snake_case) matters for these tags, so we only want to transform the path to them
  // example: trafficImpactMapping.multiSelectTags
  const trafficImpactMapping = infraCommandArgs.traffic_impact_mapping
    ? cleanObjKeysAndValues(infraCommandArgs.traffic_impact_mapping, {
        keyTransformers: [camelCase]
      })
    : undefined

  const targetDefinition = {
    targetType,
    strategyType: hasTargetDefinition ? nodeTarget.strategy_type : 'Random',
    strategy: hasTargetDefinition
      ? cleanObjKeysAndValues(
          {
            ...nodeTarget.strategy,
            ...nodeTarget.strategy.attrs
          },
          {
            keysToRemove: ['attrs', 'type']
          }
        )
      : nodeAttackConfiguration.sampling
  }

  const impactDefinition = {
    commandType: nodeAttackConfiguration.command?.infraCommandType,
    commandArgs: hasImpactDefinition
      ? {
          ...cleanObjKeysAndValues(nodeImpact.infra_command_args, {
            keysToRemove: ['type', 'traffic_impact_mapping'],
            keyTransformers: [camelCase]
          }),
          trafficImpactMapping
        }
      : {
          ...nodeAttackConfiguration.command?.infraCommandArgs
        }
  }

  if (isKubernetes && !isServiceAttackNode(node)) {
    targetDefinition.strategy.k8sObjects = (nodeTarget?.k8s_objects ?? []).map(
      (target) =>
        cleanObjKeysAndValues(target, {
          keyTransformers: [camelCase]
        })
    )
    // TODO: do you want containerSelection or container_selection ?
    impactDefinition.containerSelection = nodeTarget?.containerSelection ?? {
      selectionType: 'ANY'
    }
  }

  return {
    attackType: 'ILFI',
    attackConfiguration: nodeAttackConfiguration,
    targetDefinition,
    impactDefinition,
    ...(node &&
      'include_new_targets' in node && {
        includeNewTargets: node.include_new_targets
      }),
    ...(node &&
      'continue_on_empty_targets' in node && {
        continueOnEmptyTargets: node.continue_on_empty_targets
      })
  }
}

export function graphToStepsAPI(graph: ScenarioGraph): ScenarioStep[] {
  const nodes = arrayFromGraph(graph)
  const steps: ScenarioStep[] = []

  for (let i = 0; i < nodes.length; ++i) {
    const node = nodes[i]

    if (isDelayNode(node)) {
      continue
    }

    const step: ScenarioStep = {
      attacks: [graphNodeToStepAttack(node)]
    }

    const nextNode = nodes[i + 1]

    if (nextNode && isDelayNode(nextNode)) {
      step.delay = nextNode.delay
    }

    steps.push(step)
  }

  return steps
}

export function stepAttackToGraphNode(step) {
  const attack = step.attacks[0]
  const attackConfiguration = attack.attackConfiguration
  const attackTarget = attack.targetDefinition
  const attackImpact = attack.impactDefinition
  const isKubernetes = isTargetTypeKubernetes(attackTarget.targetType)

  const targetStrategy = {
    attrs: cleanObjKeysAndValues(attackTarget.strategy, {
      keysToRemove: [
        'percentage',
        'count',
        'allHosts',
        'allContainers',
        'k8sObjects'
      ]
    })
  }

  if (attackTarget.strategy.allHosts) {
    targetStrategy.all_hosts = true
  } else if (attackTarget.strategy.allContainers) {
    targetStrategy.all_containers = true
  }

  if (attackTarget.strategyType === 'Random') {
    if (attackTarget.strategy.percentage) {
      targetStrategy.type = 'RandomPercent'
      targetStrategy.percentage = attackTarget.strategy.percentage
    } else {
      targetStrategy.type = 'RandomCount'
      targetStrategy.count = attackTarget.strategy.count
    }
  } else {
    targetStrategy.type = 'Exact'
  }

  // Network tags are stored in an object where the key is the tag with an array of values.
  // Casing (camelCase/snake_case) matters for these tags, so we only want to transform the path to them
  // example: trafficImpactMapping.multiSelectTags
  const traffic_impact_mapping = attackImpact.commandArgs.trafficImpactMapping
    ? cleanObjKeysAndValues(attackImpact.commandArgs.trafficImpactMapping, {
        keyTransformers: [snakeCase]
      })
    : undefined

  const node = {
    attack_configuration: attackConfiguration,
    target_definition: {
      strategy_type: attackTarget.strategyType,
      target_type: attackTarget.targetType,
      strategy: targetStrategy
    },
    impact_definition: {
      infra_command_type: attackImpact.commandType,
      infra_command_args: {
        ...cleanObjKeysAndValues(attackImpact.commandArgs, {
          keysToRemove: ['commandType', 'trafficImpactMapping'],
          keyTransformers: [snakeCase]
        }),
        type: attackImpact.commandType
      }
    },
    type: ScenarioGraphNodeType.InfraAttack,
    id: step.id
  }

  if (traffic_impact_mapping) {
    node.impact_definition.infra_command_args.traffic_impact_mapping =
      traffic_impact_mapping
  }

  if (isKubernetes) {
    node.target_definition.k8s_objects = (
      attack?.targetDefinition?.strategy?.k8sObjects ?? []
    ).map((target) =>
      cleanObjKeysAndValues(target, {
        keyTransformers: [snakeCase]
      })
    )
  }
  return node
}

export function stepsToGraphAPI(steps) {
  const graph = {}
  const nodeArray = steps.reduce((stepNodes, step, index) => {
    const isLastStep = isLastIndex(steps, index)

    const node = stepAttackToGraphNode(step)

    stepNodes.push(node)

    if (!isLastStep && step.delay) {
      stepNodes.push({
        delay: step.delay,
        id: (index + 1).toString(),
        type: ScenarioGraphNodeType.Delay
      })
    }

    return stepNodes
  }, [])

  const nodes = nodeArray.map((node, index) => {
    const n = {
      ...node,
      id: `${index}`
    }
    if (!isLastIndex(nodeArray, index)) {
      n.next = `${index + 1}`
    }

    return [index, n]
  })

  graph.nodes = Object.fromEntries(nodes)
  graph.start_id = '0'

  return graph
}

// allows us to recursively strip parent from steps
export const stripParentFromSteps = (steps) => {
  const newSteps = [...steps]

  newSteps.forEach((step) => {
    delete step.parent
    if (step.branches) {
      step.branches = stripParentFromSteps(step.branches)
    }
    return step
  })

  return newSteps
}

// we do this both when adding/editing Steps and when saving scenario
// TODO: Concurrent Attacks Project: Convert to mapping Concurrent nodes instead of dividing between continuous status check and synchronous graphs
export const formatRequestBodyGraph = (steps) => {
  const nodeGraph = graphFromArray(stripParentFromSteps(steps))
  let graph

  const continuousStatusChecksGraph = getGraphNodesByType(
    nodeGraph,
    ScenarioGraphNodeType.ContinuousStatusCheck
  ).map((node) => [node])

  const synchronousGraph = getGraphNodesByType(
    nodeGraph,
    ...synchronousGraphTypes
  )

  if (continuousStatusChecksGraph.length > 0) {
    graph = concurrentGraphFromArray([
      ...continuousStatusChecksGraph,
      synchronousGraph
    ])
  } else {
    // If there are no continuous status checks,
    // there are no concurrent nodes. Remove parent parameter.
    synchronousGraph.map((step) => {
      delete step.branchId
    })
    graph = graphFromArray(synchronousGraph)
  }

  return graph
}

export const getIndentLevel = (step, previousStep) => {
  const currentBranchId = step?.branchId
  const previousBranchId = previousStep?.branchId

  const hasSameBranchId =
    currentBranchId && previousBranchId && currentBranchId === previousBranchId

  const shouldIndent =
    // indent if previous step is a continuous status check node or they share the same branch
    (isContinuousStatusCheckNode(previousStep) || hasSameBranchId) &&
    // and if the current step isn't a status check node
    !isContinuousStatusCheckNode(step)

  return shouldIndent ? 1 : 0
}

export const getInteractiveNodes = (steps) =>
  steps.filter((step) => !isDelayNode(step) && !isConcurrentNode(step))

// TODO rename to node?
export const findRunStep = (
  run: ScenarioRunResponse,
  currentStep: string,
  preferredDefault?: GraphNode | FailureFlagNode
) => {
  return run && currentStep
    ? // @ts-expect-error type mismatch
      (reformatSteps(run.graph)
        .concat(run.graph.observers ?? [])
        .find((currRun) => getStepId(currRun) === currentStep) as GraphNode)
    : preferredDefault ?? undefined
}

export const getStepId = (step) =>
  step?.guid ?? `${step?.branchId ?? ''}${step?.id}`
